import countries, { type Country } from "world-countries";
import { FALLBACK_COUNTRY, localCountryCode } from "../../common/local-country";
import { fold, rankedFilter, type SearchTerms, termRank } from "../../common/option-search";
import { DEFAULT_VIEW, VIEW_CODES } from "./shapes";

export type { Country };

// The name somebody would look for rather than the code the data is keyed on, and in English because that is what the
// labels are in: a reader's own collation would put the same list in a different order to the one the labels read in.
export const COUNTRIES: readonly Country[] = [...countries]
  .sort((a, b) => a.name.common.localeCompare(b.name.common, "en"));

// The label is the name alone: a searchable Select seeds its own search box with the label of what is selected, so a
// flag glyph in there is two codepoints no country name matches and the box stops searching the moment one is picked.
// The picker draws the flag beside the label instead, which is what `renderOption` and the left section are for.
export const COUNTRY_OPTIONS = COUNTRIES.map((country) => ({ value: country.cca2, label: country.name.common }));

// A share link carries anything at all, and so does a border: both are looked up rather than trusted.
export function findCountry(code: unknown): Country | undefined {
  return typeof code === "string" ? BY_CODE.get(code.toUpperCase()) : undefined;
}

// Total by construction, which is what makes a hash naming a country that has since gone a page that opens anyway.
export function pickCountry(code: unknown): Country {
  return findCountry(code) ?? findCountry(localCountryCode()) ?? findCountry(FALLBACK_COUNTRY) ?? COUNTRIES[0];
}

// A land border names the country in alpha-3, which is the one place the data points at itself.
export function borderCountries(country: Country): Country[] {
  return country.borders.map((code) => BY_ALPHA3.get(code)).filter((border): border is Country => border !== undefined);
}

// Both codes reach the same country, since the borders are written in alpha-3 and everything else in alpha-2.
const BY_CODE = new Map(COUNTRIES.map((country) => [country.cca2, country]));
const BY_ALPHA3 = new Map(COUNTRIES.map((country) => [country.cca3, country]));

// What the map can be drawn from: Natural Earth's own set and the thirty-one countries it authors one for, in the
// order the labels read in like the countries themselves. The default is first rather than alphabetical, being the
// one answer that is nobody's in particular. The flag is beside the label and never in it, for the reason spelled
// on COUNTRY_OPTIONS — this picker seeds no search box, but a list whose rows are drawn two ways is worse.
export const VIEW_OPTIONS = [
  { value: DEFAULT_VIEW, label: "Default" },
  ...VIEW_CODES
    .map((code) => ({ value: code, label: BY_CODE.get(code)?.name.common ?? code }))
    .sort((a, b) => a.label.localeCompare(b.label, "en")),
];

// Ranked rather than left in the alphabetical order the options arrive in: searching the translations is what lets
// "Allemagne" find Germany, but it also means "uni" matches the Estonian for kingdom in five countries nobody was
// looking for, which buried United Kingdom beneath Eswatini.
export const countryFilter = rankedFilter((code, needle) => termRank(SEARCH_TERMS.get(code), needle));

// Everything a country is called, so that AUS, 036, Oz, Côte d'Ivoire typed without the circumflex, Deutschland and
// Allemagne each reach the country somebody meant. Mantine's own filter is a substring match on the label alone,
// which answers to none of those.
const SEARCH_TERMS = new Map(COUNTRIES.map((country) => [country.cca2, searchTerms(country)]));

function searchTerms(country: Country): SearchTerms {
  const rest = [
    country.name.official,
    ...country.altSpellings,
    ...country.capital,
    ...country.tld,
    ...Object.values(country.name.native).flatMap((name) => [name.common, name.official]),
    ...Object.values(country.translations).flatMap((name) => [name.common, name.official]),
  ];
  const codes = [country.cca2, country.cca3, country.ccn3, country.cioc].filter(Boolean);
  const name = fold(country.name.common);

  return {
    name,
    codes: new Set(codes.map(fold)),
    whole: new Set([name]),
    // Whole names outrank a label's first letters, which is what puts Holland's Netherlands above the Caribbean one.
    aliases: new Set(rest.map(fold)),
    rest: [fold([country.name.common, ...codes, ...rest].join(" "))],
  };
}
